import { memo, useRef } from "react"

import { getPrefixedClassNames as pfcn, styles } from "./styles"

import { Area } from "./types"
import { GridCell } from "./GridCell"
import { HoverCell } from "./HoverCell"
import { cx } from "@emotion/css"
import loadingIcon from "assets/dots.gif"
import { useLockedRow } from "routes/Base/Plans/Details/useLockedRow"
import { useSortableRow } from "./Sortable/useSortableRow"
import { useTableContext } from "./context"

type GridRowProps = {
  area: Area
  id: string
  index: number
  start: number
  size: number
  scrolledLeft?: number
}

export const GridRow = memo(function (props: GridRowProps) {
  const { area, id, index, start, size, scrolledLeft = 0 } = props
  const rowRef = useRef<HTMLDivElement | null>(null)
  const { value: lockedRowId } = useLockedRow()

  const {
    columnManager: cm,
    getRowProps,
    hoveredRow,
    onRowHover,
    rowHoverActions,
    sortable,
    isRowSortable,
    disabled,
  } = useTableContext()
  const { className, additionDisabled, crEditDisabled } = getRowProps(index, id)

  const sortProps = useSortableRow({
    id: id,
    disabled: !sortable || !isRowSortable(id) || disabled,
  })

  const renderHoverCellHere =
    !additionDisabled &&
    !disabled &&
    !sortProps.isDragging &&
    rowHoverActions &&
    hoveredRow === index &&
    (area === "fixed" || !cm.hasFixedColumns)

  const isLocked = lockedRowId === id

  return (
    <>
      <div
        id={`grid-row-${index}`}
        data-testid={`grid-row-test`}
        key={`${id}/${size}`}
        data-area={area}
        role="row"
        ref={(el) => {
          sortProps.setNodeRef(el)
          rowRef.current = el
        }}
        className={cx(
          styles.row,
          className,
          pfcn("body-row", `body-row--${area}`),
          { [pfcn("body-row--hovered")]: hoveredRow === index },
          { [pfcn("body-row--locked")]: isLocked },
        )}
        style={{
          top: `calc(${start}px + var(--header-height, 0px))`,
          ...sortProps.style,
        }}
        onMouseEnter={
          onRowHover && !isLocked && !sortProps.isDragging
            ? () => onRowHover(index, true)
            : undefined
        }
        onMouseLeave={
          onRowHover && !isLocked && !sortProps.isDragging
            ? () => onRowHover(index, false)
            : undefined
        }
        {...sortProps.listeners}
      >
        {isLocked && area === "main" ? (
          <div className={styles.locker} style={{ left: scrolledLeft }}>
            <img width={32} src={loadingIcon} alt="calculating..." />
          </div>
        ) : null}
        {cm.getColumnsByArea(area).map((column, idx) => (
          <GridCell
            cellIndex={idx}
            key={area + id + column.id}
            rowIndex={index}
            rowId={id}
            columnId={column.id}
            area={area}
            editDisabled={crEditDisabled}
          />
        ))}
        {renderHoverCellHere && !isLocked ? (
          <HoverCell
            open={hoveredRow === index}
            rowRef={rowRef}
            rowId={id}
            rowIndex={index}
            renderer={rowHoverActions!}
          />
        ) : null}
      </div>
    </>
  )
})
